/**
 * Shared AD/SB "last performed" display (used by API scheduleRemaining and client UI).
 */

const formatHoursDisplay = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return '';
  const hours = Math.floor(n);
  let minutes = Math.round((n % 1) * 60);
  if (minutes >= 60) minutes = 59;
  if (minutes < 0) minutes = 0;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
};

/**
 * @param {Object} entry - schedule entry with recurrenceBasedOn, lastDoneValue, lastDoneNote
 * @param {Date|string|number|null|undefined} bulletinCreatedAt
 * @returns {{ display: string, tooltip: string|null }}
 */
function formatAdsbScheduleEntryLastPerformed(entry, bulletinCreatedAt) {
  const basedOn = entry.recurrenceBasedOn;
  if (!basedOn) return { display: '—', tooltip: null };
  const hasLastDone = entry.lastDoneValue != null && entry.lastDoneValue !== '';
  const note =
    entry.lastDoneNote != null && String(entry.lastDoneNote).trim()
      ? String(entry.lastDoneNote).trim()
      : null;

  if (basedOn === 'HOURS') {
    if (hasLastDone) {
      const d = formatHoursDisplay(Number(entry.lastDoneValue));
      return { display: d || '—', tooltip: note };
    }
    return { display: '0h 00m (default)', tooltip: note || 'No value set; live calc uses 0 h.' };
  }
  if (basedOn === 'CYCLES') {
    const n = hasLastDone ? Number(entry.lastDoneValue) : 0;
    if (Number.isNaN(n)) return { display: '—', tooltip: note };
    const rounded = Math.round(n * 100) / 100;
    return {
      display: hasLastDone ? `${rounded} cycles` : '0 cycles (default)',
      tooltip: note || (!hasLastDone ? 'No value set; live calc uses 0 cycles.' : null)
    };
  }
  if (basedOn === 'LANDINGS') {
    const n = hasLastDone ? Number(entry.lastDoneValue) : 0;
    if (Number.isNaN(n)) return { display: '—', tooltip: note };
    const floored = Math.floor(n);
    return {
      display: hasLastDone ? `${floored} landings` : '0 landings (default)',
      tooltip: note || (!hasLastDone ? 'No value set; live calc uses 0 landings.' : null)
    };
  }
  if (['DAYS', 'MONTHS', 'YEARS'].includes(basedOn)) {
    if (hasLastDone) {
      const d = new Date(entry.lastDoneValue);
      if (Number.isNaN(d.getTime())) return { display: '—', tooltip: note || 'Invalid last performed date.' };
      return { display: d.toISOString().slice(0, 10), tooltip: note };
    }
    const base = bulletinCreatedAt != null ? new Date(bulletinCreatedAt) : null;
    if (!base || Number.isNaN(base.getTime())) return { display: '—', tooltip: note || 'No last performed date or bulletin date.' };
    return {
      display: `${base.toISOString().slice(0, 10)} (bulletin date)`,
      tooltip: note || 'No last performed date; interval runs from bulletin created date.'
    };
  }
  return { display: '—', tooltip: note };
}

/** Decimal hours → "950h 30m" for form echo / read-only display. */
function formatAdsbLastDoneHoursDisplay(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return '';
  const hours = Math.floor(n);
  const minutes = Math.min(59, Math.max(0, Math.round((n % 1) * 60)));
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

module.exports = {
  formatAdsbScheduleEntryLastPerformed,
  formatAdsbLastDoneHoursDisplay,
  formatHoursDisplay
};
